// src/components/admin/InvitationsManagement.jsx
import React, { useState, useEffect, use, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail,
  Send,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  X,
  Eye,
  Calendar,
  User,
  Mail as MailIcon,
  Clock,
  Check,
  XCircle,
  Grid,
  List,
  Inbox,
  UserCheck,
  Shield,
  AlertTriangle,
  Bell,
  FileText,
  Download
} from 'lucide-react';
import axios from '../../utils/axios';

import Unauthorized from './UnauthorizedAccess';
import { EIC_ADMIN_ROLES } from './utils/AllowedRoles';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../utils/toast';
import RichTextViewer from '../common/RichTextViewer';
import SendReminderModal, {isReminderEligible} from './modals/SendReminderModal';
import InvitationDetailsModal from './modals/InvitationDetailsModal';

const SECTION1_SCORES = [
  'accurately_reflect_manuscript_subject_score',
  'clearly_summarize_content_score',
  'presents_what_is_known_score',
  'gives_accurate_summary_score',
  'purpose_clear_score',
  'method_section_clear_score',
  'study_materials_clearly_described_score',
  'research_method_valid_score',
  'ethical_standards_score',
  'study_find_clearly_described_score',
  'result_presented_logical_score',
  'graphics_complement_result_score',
  'table_follow_specified_standards_score',
  'tables_add_value_or_distract_score',
  'issues_with_title_score',
  'manuscript_present_summary_of_key_findings_score',
  'manuscript_highlight_strength_of_study_score',
  'manuscript_compare_findings_score',
  'manuscript_discuss_meaning_score',
  'manuscript_describes_overall_story_score',
  'conclusions_reflect_achievement_score',
  'manuscript_describe_gaps_score',
  'referencing_accurate_score'
];

const SECTION2_SCORES = [
  'novelty_score',
  'quality_score',
  'scientific_accuracy_score',
  'overall_merit_score',
  'english_level_score'
];

const getRecommendationColor = (recommendation) => {
  const colors = {
    'Accept': 'bg-green-100 text-green-800 border-green-200',
    'Accept As It Is': 'bg-green-100 text-green-800 border-green-200',
    'Accept Following Minor Revisions': 'bg-blue-100 text-blue-800 border-blue-200',
    'Minor Revision': 'bg-blue-100 text-blue-800 border-blue-200',
    'Reconsider Following Major Revisions': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Major Revision': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Reject': 'bg-red-100 text-red-800 border-red-200'
  };
  return colors[recommendation] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const sumScores = (review, fields) =>
  fields.reduce((acc, field) => acc + (parseInt(review?.[field]) || 0), 0);

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
};

// Helper to parse a date, treating MySQL date strings (YYYY-MM-DD) as local
// time so day arithmetic doesn't shift due to UTC timezone offsets.
const parseInvitationDate = (dateString) => {
  if (!dateString) return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [y, m, day] = dateString.split('-').map(Number);
    return new Date(y, m - 1, day);
  }
  const d = new Date(dateString);
  return isNaN(d) ? null : d;
};

// Extract the numeric DB id from an invitation. The title/list views use a
// formatted string id (e.g. "INV-2026-001"), while the detail endpoint returns
// the raw numeric id, so we must support both.
const getNumericInvitationId = (invitation) => {
  if (!invitation) return null;
  const id = invitation.id;
  if (id === null || id === undefined) return null;
  if (typeof id === 'number') return id;
  if (typeof id === 'string') {
    const parts = id.split('-');
    return parts[parts.length - 1];
  }
  return null;
};

const InvitationsManagement = () => {
  const navigate = useNavigate();
  const {user} = useAuth()
  // Check if user has required editorial level
    const isAuthorized = user && EIC_ADMIN_ROLES.includes(user.editorial_level || user.editorialLevel);
  
    if (!isAuthorized) {
      return (
        <Unauthorized
          message="You need editor-in-chief or admin privileges to access this feature."
        />
      );
  }
  // State for UI
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'editor', 'reviewer'
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('admin_invitations_view');
    return saved || 'table';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [remindersData, setRemindersData] = useState([]);
  const [reminderNotes, setReminderNotes] = useState('');
  const [extendExpiry, setExtendExpiry] = useState(false);
  const [extendDays, setExtendDays] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [selectedInvitations, setSelectedInvitations] = useState(new Set());
  const [showBulkReminderModal, setShowBulkReminderModal] = useState(false);

  
  // Data states
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    editor: 0,
    reviewer: 0,
    pending: 0,
    accepted: 0,
    declined: 0,
    expired: 0
  });
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10
  });

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch invitations data
  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        role: activeTab === 'all' ? undefined : activeTab,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        search: debouncedSearchTerm || undefined
      };

      const response = await axios.get('/api/semperfi/invitations', { params });

      if (response.data.success) {
        setInvitations(response.data.invitations);
        setPagination({
          total: response.data.total,
          totalPages: response.data.totalPages,
          currentPage: response.data.currentPage,
          limit: response.data.limit
        });
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
      showToast('Failed to load invitations', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats separately
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/semperfi/invitations/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Load data on mount and when filters change
  useEffect(() => {
    fetchInvitations();
  }, [currentPage, activeTab, filterStatus, debouncedSearchTerm]);

  // Save view type to localStorage
  useEffect(() => {
    localStorage.setItem('admin_invitations_view', viewType);
  }, [viewType]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      invite_sent: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      review_invitation_accepted: 'bg-green-100 text-green-800 border-green-200',
      review_submitted: 'bg-green-100 text-green-800 border-green-200',
      edit_invitation_accepted: 'bg-green-100 text-green-800 border-green-200',
      edit_submitted: 'bg-green-100 text-green-800 border-green-200',
      declined: 'bg-red-100 text-red-800 border-red-200',
      cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
      expired: 'bg-gray-100 text-gray-800 border-gray-200',
      completed: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending':
      case 'invite_sent':
        return <Clock className="w-3 h-3 mr-1" />;
      case 'accepted':
      case 'review_invitation_accepted':
      case 'edit_invitation_accepted':
        return <Check className="w-3 h-3 mr-1" />;
      case 'review_submitted':
      case 'edit_submitted':
      case 'completed':
        return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'declined':
        return <XCircle className="w-3 h-3 mr-1" />;
      case 'cancelled':
        return <XCircle className="w-3 h-3 mr-1" />;
      case 'expired':
        return <AlertCircle className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  const getRoleIcon = (role) => {
    switch(role) {
      case 'editor':
        return <Shield className="w-3 h-3 mr-1" />;
      case 'reviewer':
        return <UserCheck className="w-3 h-3 mr-1" />;
      default:
        return <User className="w-3 h-3 mr-1" />;
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      editor: 'bg-purple-100 text-purple-800 border-purple-200',
      reviewer: 'bg-blue-100 text-blue-800 border-blue-200'
    };
    return colors[role] || colors.reviewer;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      normal: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const handleViewInvitation = async (invitation) => {
    try {
      // Extract the numeric ID from the formatted ID (e.g., "INV-2026-001" -> "1")
      const numericId = getNumericInvitationId(invitation);
      
      const response = await axios.get(`/api/semperfi/invitations/${numericId}`);
      if (response.data.success) {
        // Merge the detailed data with the existing invitation data
        const detailedInvitation = {
          ...invitation,
          ...response.data.invitation,
          // Ensure we have all the fields needed for the modal
          invitedTo: response.data.invitation.invited_user_name || invitation.invitedTo,
          invitedToEmail: response.data.invitation.invited_user || invitation.invitedToEmail,
          invitedDate: formatDate(response.data.invitation.invitation_date) || invitation.invitedDate,
          expiryDate: formatDate(response.data.invitation.invitation_expiry_date) || invitation.expiryDate,
          status: response.data.invitation.invitation_status || invitation.status,
          role: response.data.invitation.invited_for === 'To Edit' ? 'editor' : 'reviewer',
          sentEmail: response.data.sentEmail || null,
          submission: response.data.invitation.submission ? {
            ...response.data.invitation.submission,
            date_submitted: formatDate(response.data.invitation.submission.date_submitted),
            discipline: response.data.invitation.submission.discipline || 'Not specified'
          } : null
        };
        
        setSelectedInvitation(detailedInvitation);

        // When a reviewer has submitted their review, the eye icon opens the
        // submitted review report instead of the plain invitation details.
        if (detailedInvitation.status === 'review_submitted' && response.data.review) {
          setReviewData(response.data.review);
          setShowReviewModal(true);
        } else {
          setReviewData(null);
          setShowViewModal(true);
        }
      }
    } catch (error) {
      console.error('Error fetching invitation details:', error);
      showToast('Failed to load invitation details', 'error');
    }
  };

  const handleResendClick = (invitation) => {
    setSelectedInvitation(invitation);
    setShowResendModal(true);
  };



  const handleViewReminders = async (invitation) => {
    if (!invitation) return;
    try {
      setRemindersLoading(true);
      const numericId = getNumericInvitationId(invitation);
      const response = await axios.get(`/api/semperfi/invitations/${numericId}/reminders`);
      if (response.data.success) {
        setRemindersData(response.data.reminders || []);
        setShowRemindersModal(true);
      } else {
        showToast('Failed to load reminder details', 'error');
      }
    } catch (error) {
      console.error('Error fetching reminders:', error);
      showToast(error.response?.data?.error || 'Failed to load reminder details', 'error');
    } finally {
      setRemindersLoading(false);
    }
  };

  const handleCancelClick = (invitation) => {
    setSelectedInvitation(invitation);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleResendConfirm = async () => {
    setResendLoading(true);
    
    try {
      const numericId = getNumericInvitationId(selectedInvitation);
      await axios.post(`/api/semperfi/invitations/${numericId}/resend`, {
        reason: 'Manual resend'
      });
      
      // Refresh the list
      await fetchInvitations();
      await fetchStats();
      
      setShowResendModal(false);
      setSelectedInvitation(null);
      
      showToast('Invitation resent successfully', 'success');
    } catch (error) {
      console.error('Error resending invitation:', error);
      showToast('Failed to resend invitation', 'error');
    } finally {
      setResendLoading(false);
    }
  };


  const handleCancelConfirm = async () => {    try {
      const numericId = getNumericInvitationId(selectedInvitation);
      await axios.post(`/api/semperfi/invitations/${numericId}/cancel`, {
        reason: cancelReason
      });
      
      // Refresh the list
      await fetchInvitations();
      await fetchStats();
      
      setShowCancelModal(false);
      setSelectedInvitation(null);
      setCancelReason('');
      
      showToast('Invitation cancelled', 'info');
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      showToast('Failed to cancel invitation', 'error');
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSearchTerm('');
    setFilterStatus('all');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setFilterStatus('all');
    setFilterType('all');
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const eligibleInvitations = invitations.filter(isReminderEligible);
  const allEligibleSelected =
    eligibleInvitations.length > 0 &&
    eligibleInvitations.every((inv) => selectedInvitations.has(inv.id));

  const toggleSelectAll = () => {
    if (allEligibleSelected) {
      setSelectedInvitations(new Set());
    } else {
      setSelectedInvitations(new Set(eligibleInvitations.map((inv) => inv.id)));
    }
  };

  const toggleSelect = (invitationId) => {
    setSelectedInvitations((prev) => {
      const next = new Set(prev);
      if (next.has(invitationId)) next.delete(invitationId);
      else next.add(invitationId);
      return next;
    });
  };

  const handleRowCheckboxChange = (invitation) => {
    if (!isReminderEligible(invitation)) {
      showToast(
        'The user has already submitted their response. Reminders cannot be sent for this invitation.',
        'info'
      );
      return;
    }
    toggleSelect(invitation.id);
  };

  const clearSelection = () => setSelectedInvitations(new Set());

// Simplified reminder opener — no more per-invitation state.
const handleReminderClick = (invitation) => {
  setSelectedInvitation(invitation);
  setShowReminderModal(true);
};

  return (
    <div className="container-fluid px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[--asfi-purple] flex items-center">
          <Mail className="w-6 h-6 mr-3" />
          Invitations Management
        </h1>
        <p className="text-gray-600 mt-1">Manage all editor and reviewer invitations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-[--asfi-purple] bg-opacity-10 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Editors</p>
              <p className="text-2xl font-bold text-purple-600">{stats.editor}</p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Reviewers</p>
              <p className="text-2xl font-bold text-blue-600">{stats.reviewer}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </div>
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Accepted</p>
              <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Declined</p>
              <p className="text-2xl font-bold text-red-600">{stats.declined}</p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Expired</p>
              <p className="text-2xl font-bold text-gray-600">{stats.expired}</p>
            </div>
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-gray-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex -mb-px">
          <button
            onClick={() => handleTabChange('all')}
            className={`py-4 px-6 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'all'
                ? 'border-[--asfi-purple] text-[--asfi-purple]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="w-4 h-4" />
            All Invitations
            <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {stats.total}
            </span>
          </button>
          
          <button
            onClick={() => handleTabChange('editor')}
            className={`py-4 px-6 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'editor'
                ? 'border-[--asfi-purple] text-[--asfi-purple]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Shield className="w-4 h-4" />
            Editor Invitations
            <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {stats.editor}
            </span>
          </button>
          
          <button
            onClick={() => handleTabChange('reviewer')}
            className={`py-4 px-6 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === 'reviewer'
                ? 'border-[--asfi-purple] text-[--asfi-purple]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserCheck className="w-4 h-4" />
            Reviewer Invitations
            <span className="ml-2 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
              {stats.reviewer}
            </span>
          </button>
        </nav>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by title, ID, or recipient..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2">
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="invite_sent">Invite Sent</option>
              <option value="accepted">Accepted</option>
              <option value="review_submitted">Review Submitted</option>
              <option value="edit_submitted">Edit Submitted</option>
              <option value="declined">Declined</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--asfi-purple] focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="Original Article">Original Article</option>
              <option value="Review">Review</option>
              <option value="Systematic Review">Systematic Review</option>
              <option value="Case Report">Case Report</option>
            </select>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewType('table')}
                className={`p-2 rounded-md transition-all ${
                  viewType === 'table' 
                    ? 'bg-white shadow-sm text-[--asfi-purple]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Table View"
              >
                <List className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-md transition-all ${
                  viewType === 'grid' 
                    ? 'bg-white shadow-sm text-[--asfi-purple]' 
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                }`}
                title="Grid View"
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>

            {/* Refresh Button */}
            <button
              onClick={clearFilters}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title="Reset Filters"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <p className="text-gray-600">
            {loading ? 'Loading...' : (
              <>
                Showing <span className="font-medium">{startIndex + 1}-{Math.min(endIndex, invitations.length)}</span> of{' '}
                <span className="font-medium">{pagination.total}</span> invitations
              </>
            )}
          </p>
          {(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
            <button
              onClick={clearFilters}
              className="text-[--asfi-purple] hover:text-[--asfi-blue] text-sm font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Bulk Selection Action Bar */}
      {selectedInvitations.size > 0 && (
        <div className="sticky top-0 z-30 mb-4 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-orange-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium text-sm">
                {selectedInvitations.size} invitation{selectedInvitations.size !== 1 ? 's' : ''} selected
              </span>
            </div>
            <button
              onClick={clearSelection}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear selection
            </button>
          </div>
          <button
            onClick={() => {
              setBulkReminderNotes('');
              setBulkExtendExpiry(false);
              setBulkExtendDays('');
              setShowBulkReminderModal(true);
            }}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2 text-sm font-medium"
          >
            <Bell className="w-4 h-4" />
            Send Reminders to {selectedInvitations.size}
          </button>
        </div>
      )}

      {/* Invitations Display */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[--asfi-purple]"></div>
        </div>
      ) : invitations.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No invitations found</h3>
          <p className="text-gray-500">
            {searchTerm || filterStatus !== 'all' || filterType !== 'all'
              ? 'Try adjusting your search or filters' 
              : 'No invitations have been sent yet.'}
          </p>
        </div>
      ) : viewType === 'table' ? (
        /* Table View */
        <div className="rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[--asfi-purple]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider w-10">
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={toggleSelectAll}
                      className={`w-4 h-4 rounded border-gray-300 text-[--asfi-purple] focus:ring-[--asfi-purple] ${
                        eligibleInvitations.length > 0
                          ? 'cursor-pointer'
                          : 'opacity-40 cursor-not-allowed'
                      }`}
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Manuscript
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={isReminderEligible(invitation) && selectedInvitations.has(invitation.id)}
                        onChange={() => handleRowCheckboxChange(invitation)}
                        title={
                          isReminderEligible(invitation)
                            ? 'Select for bulk reminder'
                            : 'Reminders cannot be sent for this invitation'
                        }
                        className={`w-4 h-4 rounded border-gray-300 text-[--asfi-purple] focus:ring-[--asfi-purple] ${
                          isReminderEligible(invitation)
                            ? 'cursor-pointer'
                            : 'opacity-40 cursor-not-allowed'
                        }`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(invitation.status)}`}>
                        {getStatusIcon(invitation.status)}
                        {invitation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getRoleColor(invitation.role)}`}>
                        {getRoleIcon(invitation.role)}
                        {invitation.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {invitation.id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900 line-clamp-2 max-w-xs">
                        {invitation.title}
                      </div>
                      <div className="text-xs text-gray-500">{invitation.manuscriptId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{invitation.invitedTo}</div>
                      <div className="text-xs text-gray-500">{invitation.invitedToEmail}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {invitation.expiryDate}
                      {invitation.reminderCount > 0 && (
                        <button
                          onClick={() => handleViewReminders(invitation)}
                          className="ml-2 text-xs text-orange-600 hover:text-orange-800 hover:underline"
                          title="View reminder details"
                        >
                          ({invitation.reminderCount})
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewInvitation(invitation)}
                          className="p-2 text-gray-600 hover:text-white hover:bg-[--asfi-purple] hover:bg-opacity-10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {(invitation.status === 'pending' || invitation.status === 'invite_sent' || invitation.status === 'accepted' || invitation.status === 'expired') && (
                          <>
                            <button
                              onClick={() => handleReminderClick(invitation)}
                              className="p-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                              title="Send Reminder"
                            >
                              <Bell className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResendClick(invitation)}
                              className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Resend"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleCancelClick(invitation)}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Grid View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isReminderEligible(invitation) && selectedInvitations.has(invitation.id)}
                      onChange={() => handleRowCheckboxChange(invitation)}
                      title={
                        isReminderEligible(invitation)
                          ? 'Select for bulk reminder'
                          : 'Reminders cannot be sent for this invitation'
                      }
                      className={`w-4 h-4 rounded border-gray-300 text-[--asfi-purple] focus:ring-[--asfi-purple] ${
                        isReminderEligible(invitation)
                          ? 'cursor-pointer'
                          : 'opacity-40 cursor-not-allowed'
                      }`}
                    />
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getRoleColor(invitation.role)}`}>
                      {getRoleIcon(invitation.role)}
                      {invitation.role}
                    </span>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(invitation.status)}`}>
                    {getStatusIcon(invitation.status)}
                    {invitation.status}
                  </span>
                </div>

                {/* Title and ID */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                  {invitation.title}
                </h3>
                <p className="text-sm text-[--asfi-purple] font-medium mb-3">
                  {invitation.manuscriptId}
                </p>

                {/* Recipient */}
                <div className="flex items-start mb-3">
                  <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-700">{invitation.invitedTo}</p>
                    <p className="text-xs text-gray-500">{invitation.invitedToEmail}</p>
                  </div>
                </div>

                {/* Dates and Priority */}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                  <div className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    Exp: {invitation.expiryDate}
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(invitation.priority)}`}>
                    {invitation.priority}
                  </span>
                </div>

                {/* Reminder Count */}
                {invitation.reminderCount > 0 && (
                  <button
                    onClick={() => handleViewReminders(invitation)}
                    className="mb-3 text-xs text-orange-600 hover:text-orange-800 hover:underline"
                    title="View reminder details"
                  >
                    {invitation.reminderCount} reminder(s) sent
                  </button>
                )}

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleViewInvitation(invitation)}
                    className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Eye className="w-4 h-4" />
                    View
                  </button>
                  
                  {(invitation.status === 'pending' || invitation.status === 'invite_sent' || invitation.status === 'accepted' || invitation.status === 'expired') && (
                    <>
                      <button
                        onClick={() => handleReminderClick(invitation)}
                        className="p-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
                        title="Send Reminder"
                      >
                        <Bell className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleResendClick(invitation)}
                        className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Resend
                      </button>
                      <button
                        onClick={() => handleCancelClick(invitation)}
                        className="p-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                        title="Cancel"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {pagination.totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm ${
                      currentPage === pageNum
                        ? 'bg-[--asfi-purple] text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
              className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* View Invitation Modal */}
{showViewModal && selectedInvitation && (
  <InvitationDetailsModal
    invitation={selectedInvitation}
    onClose={() => {
      setShowViewModal(false);
      setSelectedInvitation(null);
      setReviewData(null);
    }}
    onSendReminder={(inv) => {
      setSelectedInvitation(inv);
      setShowReminderModal(true);
    }}
    onResend={(inv) => {
      setSelectedInvitation(inv);
      setShowResendModal(true);
    }}
    onCancel={(inv) => {
      setSelectedInvitation(inv);
      setShowCancelModal(true);
    }}
    onViewReminders={(inv) => handleViewReminders(inv)}
  />
)}



      {/* Resend Confirmation Modal */}
      {showResendModal && selectedInvitation && (
        <div className="fixed  inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Send className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Resend Invitation</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Resend this invitation to {selectedInvitation.invitedToEmail}
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Manuscript:</p>
                <p className="text-sm text-gray-900 mt-1">{selectedInvitation.title}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedInvitation.manuscriptId}</p>
              </div>

              {/* <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> This will send another invitation email to the recipient.
                  </span>
                </p>
              </div> */}

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowResendModal(false)}
                  disabled={resendLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResendConfirm}
                  disabled={resendLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resendLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Resend
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Cancel Invitation</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to cancel this invitation?
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Recipient:</p>
                <p className="text-sm text-gray-900 mt-1">{selectedInvitation.invitedTo}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedInvitation.invitedToEmail}</p>
                <p className="text-sm font-medium text-gray-700 mt-3">Manuscript:</p>
                <p className="text-sm text-gray-900 mt-1">{selectedInvitation.title}</p>
              </div>

              <div className="mb-4">
                <label htmlFor="cancelReason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Cancelling (Optional)
                </label>
                <textarea
                  id="cancelReason"
                  rows="3"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please provide a reason for cancelling..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> This action cannot be undone. The recipient will no longer be able to respond to this invitation.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Keep Invitation
                </button>
                <button
                  onClick={handleCancelConfirm}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Cancel Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submitted Review Modal */}
      {showReviewModal && selectedInvitation && reviewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Submitted Review</h3>
                    <p className="text-sm text-gray-500">
                      {selectedInvitation.manuscriptId} · {selectedInvitation.title}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedInvitation(null);
                    setReviewData(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Reviewer */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="text-sm text-gray-500">Reviewer</p>
                  <p className="font-semibold text-gray-900">{reviewData.reviewer_name || 'Reviewer'}</p>
                  <p className="text-sm text-gray-600">{reviewData.reviewer_email}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getRecommendationColor(reviewData.overall_recommendation)}`}>
                    {reviewData.overall_recommendation || 'Not specified'}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Review Submitted
                  </span>
                </div>
              </div>

              {/* Scores */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Section 1 Score</p>
                  <p className="text-2xl font-bold text-purple-700">{sumScores(reviewData, SECTION1_SCORES)}</p>
                  <p className="text-xs text-gray-400">out of {SECTION1_SCORES.length * 5}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Section 2 Score</p>
                  <p className="text-2xl font-bold text-purple-700">{sumScores(reviewData, SECTION2_SCORES)}</p>
                  <p className="text-xs text-gray-400">out of {SECTION2_SCORES.length * 5}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-500">Total Score</p>
                  <p className="text-2xl font-bold text-purple-700">{sumScores(reviewData, SECTION1_SCORES) + sumScores(reviewData, SECTION2_SCORES)}</p>
                  <p className="text-xs text-gray-400">out of {(SECTION1_SCORES.length + SECTION2_SCORES.length) * 5}</p>
                </div>
              </div>

              {/* Comments */}
              <div className="space-y-4 mb-6">
                {[
                  ['one_paragraph_comment', 'Summary of Manuscript'],
                  ['general_comment', 'General Comments'],
                  ['specific_comment', 'Specific Comments'],
                  ['letter_to_editor', 'Letter to the Editor']
                ].filter(([key]) => reviewData[key]).map(([key, label]) => (
                  <div key={key} className={key === 'letter_to_editor' ? 'bg-gray-50 border border-gray-200 rounded-lg p-4' : ''}>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">{label}</p>
                    <div className="prose max-w-none text-sm text-gray-700">
                      <RichTextViewer content={reviewData[key]} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Review files */}
              {[
                ['one_paragraph_file', 'Summary File'],
                ['general_comment_file', 'General Comments File'],
                ['specific_comment_file', 'Specific Comments File']
              ].filter(([key]) => reviewData[key]).length > 0 && (
                <div className="space-y-2">
                  {[
                    ['one_paragraph_file', 'Summary File'],
                    ['general_comment_file', 'General Comments File'],
                    ['specific_comment_file', 'Specific Comments File']
                  ].filter(([key]) => reviewData[key]).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center min-w-0">
                        <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{label}</span>
                      </div>
                      <button
                        onClick={() => {
                          try {
                            const link = document.createElement('a');
                            link.href = `${process.env.REACT_APP_BACKEND_URL}/doc?url=${reviewData[key]}&download=true&filename=${selectedInvitation.manuscriptId}_${reviewData.review_id}_${key}`;
                            link.setAttribute('download', '');
                            link.style.display = 'none';
                            document.body.appendChild(link);
                            link.click();
                            setTimeout(() => document.body.removeChild(link), 100);
                          } catch (error) {
                            console.error('Error downloading file:', error);
                            showToast('Failed to download file. Please try again.', 'error');
                          }
                        }}
                        className="p-1 text-gray-500 hover:text-[--asfi-purple] hover:bg-white rounded transition-colors flex-shrink-0"
                        title={`Download ${label}`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setSelectedInvitation(null);
                    setReviewData(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reminder Confirmation Modal */}
   {showReminderModal && selectedInvitation && (
  <SendReminderModal
    invitations={[selectedInvitation]}
    onClose={() => {
      setShowReminderModal(false);
      setSelectedInvitation(null);
    }}
    onSuccess={async () => {
      await fetchInvitations();
      await fetchStats();
    }}
  />
)}

      {/* Bulk Reminder Confirmation Modal */}
   {showBulkReminderModal && (
  <SendReminderModal
    invitations={invitations.filter((inv) => selectedInvitations.has(inv.id))}
    onClose={() => {
      setShowBulkReminderModal(false);
      setSelectedInvitations(new Set());
    }}
    onSuccess={async () => {
      await fetchInvitations();
      await fetchStats();
    }}
  />
)}

      {/* Reminders Details Modal */}
      {showRemindersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reminders Sent</h3>
                {selectedInvitation && (
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedInvitation.title} ({selectedInvitation.manuscriptId})
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowRemindersModal(false);
                  setRemindersData([]);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              {remindersLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-solid border-orange-600 border-r-transparent"></div>
                </div>
              ) : remindersData.length > 0 ? (
                <div className="space-y-4">
                  {remindersData.map((reminder) => (
                    <div key={reminder.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Bell className="w-4 h-4 text-orange-600" />
                          <h4 className="text-sm font-semibold text-gray-900">
                            Reminder #{reminder.reminder_number}
                          </h4>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            {reminder.reminder_type || 'manual'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${reminder.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
                            {reminder.status}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {formatDate(reminder.sent_at) || reminder.sent_at}
                        </span>
                      </div>

                      <div className="px-4 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3">
                          <div>
                            <p className="text-gray-500">Reviewer</p>
                            <p className="text-gray-800 font-medium truncate">{reminder.reviewer_email}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Due Date</p>
                            <p className="text-gray-800 font-medium">{formatDate(reminder.due_date) || '—'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Days Overdue</p>
                            <p className="text-gray-800 font-medium">{reminder.days_overdue ?? '—'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Response</p>
                            <p className="text-gray-800 font-medium">{reminder.response_received || '—'}</p>
                          </div>
                        </div>

                        {reminder.email_subject && (
                          <div className="mb-3">
                            <p className="text-xs text-gray-500 mb-1">Subject</p>
                            <p className="text-sm font-medium text-gray-800">{reminder.email_subject}</p>
                          </div>
                        )}

                        {reminder.notes && (
                          <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-xs text-yellow-700"><strong>Notes / Custom message:</strong></p>
                            <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{reminder.notes}</p>
                          </div>
                        )}

                        <div>
                          <p className="text-xs text-gray-500 mb-1">Email Content</p>
                          {reminder.email_body ? (
                            <div className="border border-gray-200 rounded-lg p-3 bg-white">
                              <RichTextViewer content={reminder.email_body} />
                            </div>
                          ) : (
                            <p className="text-sm text-gray-400">No email content recorded</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-8">No reminders have been sent for this invitation</p>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => {
                  setShowRemindersModal(false);
                  setRemindersData([]);
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default InvitationsManagement;